import Grid from "@mui/material/Grid";
import type { GridPaginationModel } from "@mui/x-data-grid";
import React, { Suspense, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import ShimmerLoader from "@/components/common/ShimmerLoader";
import ShimmerDataGrid from "@/components/common/shimmer-data-grid";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import BreadcrumbsComponent from "@/components/Breadcrumbs";
import TurnAroundDetailHeader from "@/components/turnaround/TurnAroundDetail/TurnAroundDetailHeader";
import TurnAroundDetailGrid from "@/components/turnaround/TurnAroundDetail/TurnAroundDetailGrid";
import { fetchTurnaroundDetail } from "@/store/slices/turnaroundDetailSlice";
import DeleteDevicesConfirm from "@/components/turnaround/DeleteDevicesConfirm";
import MessageModal from "@/components/common/modals/MessageModal";
import { selectUserId, selectSelectedSiteId } from "@/store/selectors";

const TurnAroundDetail = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();

  // Redux selectors
  const userId = useAppSelector(selectUserId);
  const siteId = useAppSelector(selectSelectedSiteId);
  const turnaroundDetail = useAppSelector((state) => 
    id ? state.turnaroundDetail.details[id] : undefined
  );
  
  const turnaroundData = turnaroundDetail?.data || null;
  const status = turnaroundDetail?.status || 'idle';
  const fetched = turnaroundDetail?.fetched || false;

  // Shared pagination state
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 100,
  });

  // Page-level UI state only
  const [filters, setFilters] = useState<any>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deviceIdsToDelete, setDeviceIdsToDelete] = useState<string[]>([]);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Centralized fetch function - only place that fetches turnaround detail data
  const fetchData = useCallback(async () => {
    if (id && userId && siteId) {
      try {
        const accessToken = await getAccessTokenSilently();
        dispatch(
          fetchTurnaroundDetail({
            turnaroundId: id,
            accessToken: accessToken
          })
        );
      } catch (error) {
        console.error("Failed to fetch turnaround detail:", error);
      }
    }
  }, [id, userId, siteId, dispatch]);

  // Initial data fetch - only fetch if turnaround ID not in store
  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id,fetchData]);

  // Handler for grid callback to delete devices
  const handleBulkDelete = (deviceIds: string[]) => {
    setDeviceIdsToDelete(deviceIds);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteSuccess = async () => {
    setDeleteConfirmOpen(false);
    setDeviceIdsToDelete([]);

    // Refetch data after successful deletion
    await fetchData();

    setSuccessMessage(
      t("devicesDeletedSuccessfully", "Devices deleted successfully.")
    );
    setSuccessModalOpen(true);
  };

  const filterDevices = (filters: any) => {
    setFilters(filters);
  };

  const handleRefreshTurnaroundDetails = async () => {
    await fetchData();
  };

  return (
    <div className="page page-device-list">
      <BreadcrumbsComponent />
      <div className="page-content">
        <div data-testid="device-list-root">
          <Grid container spacing={2}>
            <Grid size={12}>
              <Suspense
                fallback={
                  <ShimmerLoader variant="text" lines={3} height="14px" />
                }
              >
                <div data-lazy-component="TurnAroundDetailHeader">
                  <TurnAroundDetailHeader
                    filterDevices={filterDevices}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    refreshTurnAroundDetails={handleRefreshTurnaroundDetails}
                    turnaroundData={turnaroundData}
                  />
                </div>
              </Suspense>
            </Grid>
          </Grid>
          <Suspense fallback={<ShimmerDataGrid />}>
            <div data-lazy-component="TurnAroundDetailGrid">
              <TurnAroundDetailGrid
                turnaroundId={id}
                userId={userId || null}
                filters={filters}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                onBulkDelete={handleBulkDelete}
              />
            </div>
          </Suspense>
          {deleteConfirmOpen && (
            <DeleteDevicesConfirm
              open={deleteConfirmOpen}
              deviceIds={deviceIdsToDelete}
              turnaroundId={id || ""}
              onClose={() => setDeleteConfirmOpen(false)}
              onSuccess={handleDeleteSuccess}
            />
          )}
          <MessageModal
            open={successModalOpen}
            onClose={() => setSuccessModalOpen(false)}
            title={t("success", "Success")}
            message={successMessage}
          />
        </div>
      </div>
    </div>
  );
};
export default TurnAroundDetail;
